import React, { useMemo } from 'react';
import { useDraggable, useDroppable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { Folder, File, HardDrive } from 'lucide-react';
import { FileStat, FolderSizeResult } from '../../api/IFileSystem';
import { useFileIcon } from '../../hooks/useFileIcon';
import { processDropItems, formatDate, formatSize } from '../../utils/fileUtils';
import { t } from '../../i18n';
import { PercentBar } from '../UI/PercentBar';
import { FileSearchStatus } from '../../../../types';

interface FileItemProps {
  file: FileStat;
  selected: boolean;
  renaming: boolean;
  isGhost?: boolean; // New prop for multi-drag visual
  viewMode: 'grid' | 'details' | 'list';
  folderSize?: FolderSizeResult | null; // Folder size info (only for directories)
  parentFolderSize?: FolderSizeResult | null; // Parent folder size (for calculating percentage in list mode)
  isSearchEngineReady?: boolean; // Whether file search engine is ready
  searchEngineStatus?: FileSearchStatus; // Detailed search engine status
  openSearchEngineInitModal?: (isReinit?: boolean) => void; // Open search engine init modal
  onClick: (e: React.MouseEvent) => void;
  onDoubleClick: () => void;
  onContextMenu: (e: React.MouseEvent) => void;
  onDropFiles?: (files: { file: File, relativePath?: string }[], targetPath: string) => void;
  children?: React.ReactNode;
}

export const FileItem: React.FC<FileItemProps> = ({
  file, selected, renaming, isGhost, viewMode, folderSize, parentFolderSize, isSearchEngineReady, searchEngineStatus, openSearchEngineInitModal, onClick, onDoubleClick, onContextMenu, onDropFiles, children
}) => {
  const { attributes, listeners, setNodeRef: setDragRef, transform, isDragging } = useDraggable({
    id: file.path,
    data: { file }
  });

  const { setNodeRef: setDropRef, isOver } = useDroppable({
    id: file.path,
    data: { file },
    disabled: file.type === 'file' 
  });
  
  const [isNativeOver, setIsNativeOver] = React.useState(false);

  const handleNativeDragOver = (e: React.DragEvent) => {
      if (file.type !== 'dir') return;
      if (e.dataTransfer.types.includes('Files')) {
          e.preventDefault();
          e.stopPropagation();
          setIsNativeOver(true);
      }
  };

  const handleNativeDragLeave = (e: React.DragEvent) => {
      if (file.type !== 'dir') return;
      e.preventDefault();
      setIsNativeOver(false);
  };

  const handleNativeDrop = async (e: React.DragEvent) => {
      if (file.type !== 'dir') return;
      if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
          e.preventDefault();
          e.stopPropagation();
          setIsNativeOver(false);
          
          if (onDropFiles) {
              const flatFiles = await processDropItems(e.dataTransfer.items);
              onDropFiles(flatFiles, file.path);
          }
      } else if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
          e.preventDefault();
          e.stopPropagation();
          setIsNativeOver(false);
          if (onDropFiles) {
              const files = Array.from(e.dataTransfer.files).map(f => ({ file: f, relativePath: f.name }));
              onDropFiles(files, file.path);
          }
      }
  };

  const style = {
    transform: CSS.Translate.toString(transform),
    opacity: isDragging ? 0 : (isGhost ? 0.4 : 1), // Hide active drag, dim others
  };

  // Combine refs
  const setNodeRef = (node: HTMLElement | null) => {
    setDragRef(node);
    setDropRef(node);
  };

  const renderIcon = (type: string) => {
    const size = iconSize;
    switch (type) {
      case 'dir': return <Folder size={size} className="text-yellow-500 fill-yellow-500/20" />;
      case 'drive': return <HardDrive size={size} className="text-slate-400" />;
      default: return <File size={size} className="text-blue-400/80" />;
    }
  };

  // Performance fix: Disable transition during drag
  const transitionClass = isDragging ? '' : 'transition-all duration-200';

  const baseClasses = `
    rounded cursor-default border ${transitionClass}
    ${selected ? 'bg-blue-500/20 border-blue-500/30' : 'border-transparent hover:bg-white/5 hover:border-white/10'}
    ${(isOver && !isDragging) || isNativeOver ? 'bg-blue-500/40 border-blue-500' : ''}
  `;

  const typeLabel = file.type === 'dir' ? t('fileFolder') : file.type === 'drive' ? t('drive') : t('file');

  // Generate tooltip with folder size info
  const tooltip = useMemo(() => {
    const lines = [
      `${t('name')}: ${file.name}`,
      `${t('type')}: ${typeLabel}`,
    ];

    let itemSize = 0;
    if (file.type === 'dir' && folderSize) {
      itemSize = folderSize.total_size;
      lines.push(`${t('size')}: ${formatSize(itemSize)}`);
    } else if (file.type === 'file') {
      itemSize = file.size;
      lines.push(`${t('size')}: ${formatSize(itemSize)}`);
    } else {
      lines.push(`${t('size')}: ${t('na')}`);
    }

    // Calculate percentage relative to current folder
    if (parentFolderSize && parentFolderSize.total_size > 0 && itemSize > 0) {
      const percent = (itemSize / parentFolderSize.total_size) * 100;
      lines.push(`${t('parentPercent')}: ${percent.toFixed(2)}%`);
    }

    if (file.type === 'dir' && folderSize) {
       lines.push(`${t('fileCount')}: ${folderSize.file_count.toLocaleString()}`);
    }

    lines.push(`${t('dateModified')}: ${formatDate(file.updatedAt)}`);
    return lines.join('\n');
  }, [file, typeLabel, folderSize, parentFolderSize]);

  const iconSize = viewMode === 'grid' ? 64 : 32;
  const iconUrl = useFileIcon(file, iconSize);

  if (viewMode === 'grid') {
    return (
      <div
        ref={setNodeRef}
        style={style}
        {...attributes}
        {...listeners}
        onClick={onClick}
        onDoubleClick={onDoubleClick}
        onContextMenu={onContextMenu}
        onDragOver={handleNativeDragOver}
        onDragLeave={handleNativeDragLeave}
        onDrop={handleNativeDrop}
        data-path={file.path}
        title={tooltip}
        className={`file-item flex flex-col items-center p-2 ${baseClasses}`}
      >
        <div className="mb-2">
          {iconUrl ? (
            <img src={iconUrl} alt={file.name} className="w-16 h-16 object-contain" draggable={false} />
          ) : (
            renderIcon(file.type)
          )}
        </div>
        {renaming ? children : (
          <span className="text-xs text-center break-all line-clamp-2 px-1">{file.name}</span>
        )}
      </div>
    );
  }

  // Size display for list mode
  const sizeDisplay = file.type === 'file'
    ? formatSize(file.size)
    : folderSize
      ? formatSize(folderSize.total_size)
      : '-';

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      onClick={onClick}
      onDoubleClick={onDoubleClick}
      onContextMenu={onContextMenu}
      onDragOver={handleNativeDragOver}
      onDragLeave={handleNativeDragLeave}
      onDrop={handleNativeDrop}
      data-path={file.path}
      title={tooltip}
      className={`file-item flex items-center px-4 py-1.5 ${baseClasses}`}
    >
      {/* Icon */}
      <div className="mr-3 shrink-0">
        {iconUrl ? (
          <img src={iconUrl} alt={file.name} className="w-5 h-5 object-contain" draggable={false} />
        ) : (
          renderIcon(file.type)
        )}
      </div>

      {/* Name */}
      <div className="flex-1 min-w-0 mr-2">
        {renaming ? children : <span className="text-sm truncate block">{file.name}</span>}
      </div>

      {/* Size */}
      <div className="w-20 text-right text-xs text-white/60 shrink-0">
        {sizeDisplay}
      </div>

      {/* Date (hidden on small screens) */}
      <div className="w-28 text-right text-xs text-white/60 shrink-0 hidden sm:block">
        {formatDate(file.updatedAt)}
      </div>

      {/* Parent folder percentage (file or folder size / parent folder size) */}
      <div className="w-24 shrink-0 pl-2">
        {file.type === 'dir' && !isSearchEngineReady ? (
          // Search engine not ready - show status based on current state
          searchEngineStatus === FileSearchStatus.UNINITIALIZED ? (
            // Not initialized - show clickable "pending init" button
            openSearchEngineInitModal ? (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  openSearchEngineInitModal(false);
                }}
                className="text-xs text-yellow-500/80 hover:text-yellow-400 hover:underline cursor-pointer"
              >
                {t('percentPendingInit')}
              </button>
            ) : (
              <span className="text-xs text-yellow-500/80">{t('percentPendingInit')}</span>
            )
          ) : (
            // Initializing (INDEXING/BUILDING_INDEX/CALCULATING_SIZES) - show non-clickable text
            <span className="text-xs text-slate-400">{t('percentInitializing')}</span>
          )
        ) : (
          // Normal percentage display
          parentFolderSize && parentFolderSize.total_size > 0 && (() => {
            const itemSize = file.type === 'dir' && folderSize
              ? folderSize.total_size
              : file.type === 'file'
                ? file.size
                : 0;
            const percent = (itemSize / parentFolderSize.total_size) * 100;
            return itemSize > 0 ? <PercentBar percent={percent} size="sm" /> : null;
          })()
        )}
      </div>
    </div>
  );
};
